import { computed, markRaw, type Component } from 'vue'
import {
  mdiAccountAlert,
  mdiClockAlertOutline,
  mdiClockCheckOutline,
  mdiClockEnd,
  mdiLock,
  mdiLockOpenVariant,
  mdiTextBoxCheck
} from '@mdi/js'
import { classifyNpsScore } from '@/utils/nps'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import { getDutyTagBgClass } from '@/utils/doctorStation'
import PermanenceRecordIcon from '@/assets/icons/checks/permanence_registro.vue'
import {
  DoctorStationStatus,
  doctorStationStatusTranslate
} from '@/translations/doctor.station.status'
import {
  ExceptionSummaryStatus,
  type PostStationCellStatus
} from '@/Network/Types/Responses/StationResponses'

export type StationRowCellType = 'FIXO' | 'ABERTO' | 'COBERTURA' | 'EXTRA'

export type StationRowStyleInput = {
  tableType: StationRowCellType
  solid: boolean
  ourTeam: boolean
  isAlert: boolean
  colorOverride: { stripe: string; bg: string } | null
  requiredCheckOurTeam?: boolean
  requiredCheckNotOurTeam?: boolean
  exceptionSummaryStatus: ExceptionSummaryStatus | null
  postStationStatus: PostStationCellStatus | null
  npsScore: number | null
  hasObservations: boolean
  doctorPending: boolean
  doctorConfirmed: boolean
  doctorStationStatus: DoctorStationStatus
  permanenceTooltip: string | null
}

/** Ícone de status do card: `path` (mdi, via v-icon) ou `component` (ícones de check). */
export type StationStatusIcon = {
  key: string
  label: string
  path?: string
  component?: Component
  color?: string
  testId?: string
}

type RowColors = { stripe: string; bg: string; stripeStyle?: { background: string } }

const STATUS_TRANSLATE = doctorStationStatusTranslate as Partial<
  Record<DoctorStationStatus, { text: string; icon: Component }>
>
const PERMANENCE_ICON = markRaw(PermanenceRecordIcon)

const EXCEPTION_TOOLTIPS: Record<string, string> = {
  APPROVED: 'Excepcionalidade aprovada',
  IN_PROGRESS: 'Excepcionalidade em andamento',
  IN_REVIEW: 'Excepcionalidade em revisão'
}
const POST_STATION_TOOLTIPS: Record<PostStationCellStatus, string> = {
  APPROVED: 'Avaliação aprovada',
  REVIEW: 'Revisar avaliação'
}

/**
 * Cor, alerta e ícones de status do card de plantão. Extraído do `withIcon.vue` (grade
 * desktop) para o `StationListCard.vue` (celular) usar a mesma regra, sem duplicar.
 */
export function useStationRowStyle(source: () => StationRowStyleInput) {
  const { flags } = useFeatureFlags()

  const stationIsAlert = computed(() => !!flags.enabledDsAttendancePolicy && !!source().isAlert)

  const npsRingStyle = computed(() => {
    const score = source().npsScore
    if (score == null) return {}
    return { outline: `2px solid ${classifyNpsScore(score).color}` }
  })

  const baseColors = computed<{ stripe: string; bg: string }>(() => {
    const input = source()
    if (stationIsAlert.value) return { stripe: 'bg-negative', bg: '#DA1E28' }
    if (!input.ourTeam) return { stripe: 'bg-neutralLight', bg: '#F0F0F4' }
    const colors: Record<StationRowCellType, { stripe: string; bg: string }> = {
      FIXO: { stripe: 'bg-primary10', bg: '#F7FCFD' },
      COBERTURA: { stripe: 'bg-tertiary', bg: '#FDF7F8' },
      EXTRA: { stripe: 'bg-feedBackSuccess', bg: '#F6FDF9' },
      ABERTO: { stripe: 'bg-feedBackAlert', bg: input.solid ? '#F18F1B' : '#FEF3E7' }
    }
    return colors[input.tableType ?? 'EXTRA']
  })

  // Lentes (Colorir por Setor/Especialidade/Check) sobrescrevem a cor; o alerta vermelho continua prevalecendo.
  const colorDefination = computed<RowColors>(() => {
    const base = baseColors.value
    const override = source().colorOverride
    if (!override || stationIsAlert.value) return { ...base, stripeStyle: undefined }
    return { stripe: '', bg: override.bg, stripeStyle: { background: override.stripe } }
  })

  const defineOurTeamBgColor = computed(() => getDutyTagBgClass(source().tableType))
  const isAlertTextColor = computed(() => (stationIsAlert.value ? 'text-white' : ''))

  const notShowStatusIcon = computed(() => {
    const input = source()
    return input.ourTeam ? !input.requiredCheckOurTeam : !input.requiredCheckNotOurTeam
  })

  const exceptionIconPath = computed(() =>
    [null, ExceptionSummaryStatus.IN_PROGRESS].includes(source().exceptionSummaryStatus)
      ? mdiLock
      : mdiLockOpenVariant
  )

  const exceptionIconColor = computed(() => {
    switch (source().exceptionSummaryStatus) {
      case ExceptionSummaryStatus.APPROVED:
        return '#DA1E28'
      case ExceptionSummaryStatus.IN_PROGRESS:
        return '#F18F1B'
      case ExceptionSummaryStatus.IN_REVIEW:
        return '#F1C21B'
      default:
        return undefined
    }
  })

  const exceptionTooltipText = computed(() => {
    const status = source().exceptionSummaryStatus
    return status ? EXCEPTION_TOOLTIPS[status] : ''
  })

  // Hexes espelham o tema Vuetify (success/alert em src/app.ts), mesmas cores
  // resolvidas pelas Legendas (grupo "Pós-Plantão" no legends.modal.vue).
  const postStationIconColor = computed(() => {
    switch (source().postStationStatus) {
      case 'APPROVED':
        return '#198038'
      case 'REVIEW':
        return '#F18F1B'
      default:
        return undefined
    }
  })

  const postStationTooltipText = computed(() => {
    const status = source().postStationStatus
    return status ? POST_STATION_TOOLTIPS[status] : ''
  })

  // Ícones atrás de flag (pós-plantão e permanência), sempre no fim da lista.
  const flagGatedIcons = computed<StationStatusIcon[]>(() => {
    const input = source()
    const icons: StationStatusIcon[] = []
    if (flags.enabledPostStation && input.postStationStatus)
      icons.push({
        key: 'post-station',
        label: postStationTooltipText.value,
        path: mdiClockEnd,
        color: postStationIconColor.value,
        testId: 'post-station-status-icon'
      })
    if (flags.enabledPermanenceRecord && input.permanenceTooltip)
      icons.push({
        key: 'permanence',
        label: input.permanenceTooltip,
        component: PERMANENCE_ICON,
        testId: 'permanence-record-icon'
      })
    return icons
  })

  /** Mesmos ícones, condições e ordem do `withIcon.vue`. */
  const statusIcons = computed<StationStatusIcon[]>(() => {
    const input = source()
    const icons: StationStatusIcon[] = []
    if (input.hasObservations)
      icons.push({
        key: 'observations',
        label: 'Existe observações',
        path: mdiTextBoxCheck,
        color: 'warning'
      })
    if (input.doctorPending)
      icons.push({
        key: 'doctor-pending',
        label: 'Médico pendente',
        path: mdiAccountAlert,
        color: 'primary'
      })
    icons.push(
      input.doctorConfirmed
        ? {
            key: 'confirmed',
            label: 'Plantão confirmado',
            path: mdiClockCheckOutline,
            color: 'success'
          }
        : {
            key: 'pending',
            label: 'Plantão pendente',
            path: mdiClockAlertOutline,
            color: 'neutral'
          }
    )
    const status = STATUS_TRANSLATE[input.doctorStationStatus]
    if (
      input.doctorStationStatus !== DoctorStationStatus.SEM_STATUS &&
      !notShowStatusIcon.value &&
      status
    )
      icons.push({ key: 'check-status', label: status.text, component: markRaw(status.icon) })
    if (input.exceptionSummaryStatus)
      icons.push({
        key: 'exception',
        label: exceptionTooltipText.value,
        path: exceptionIconPath.value,
        color: exceptionIconColor.value
      })
    return [...icons, ...flagGatedIcons.value]
  })

  return {
    stationIsAlert,
    npsRingStyle,
    colorDefination,
    defineOurTeamBgColor,
    isAlertTextColor,
    notShowStatusIcon,
    exceptionIconPath,
    exceptionIconColor,
    exceptionTooltipText,
    postStationIconColor,
    postStationTooltipText,
    statusIcons
  }
}
